import type { ReactNode } from 'react';
import { fmtDayMonth, fmtMoneyWhole, fmtTime } from '../../flight/results/flight_format.js';
import { carrierNameOf } from '../../flight/results/flight_offer_source.js';
import type { ConciergeCopy } from '../model/checkout_copy.js';
import type { ConciergeRequestSource } from '../model/checkout_source.js';
import { RequestServiceRows, type RequestServiceRowItem } from '../request/request_service_rows.js';

/*
 * O PEDIDO offline em texto pronto para as peças do pedido: título,
 * janela, total e as linhas do painel "Sua viagem". Duas telas as desenham: o
 * checkout do pedido (antes do envio) e a tela "Solicitação enviada" do host
 * (depois) — o mesmo item, nos dois momentos, sem virar `CheckoutItem` (um item
 * sem oferta e sem preço não cabe no recibo do checkout online; ver
 * `concierge_checkout_schema.ts`).
 */

/** "24 – 26 set" / "30 set – 2 out": uma janela de dias (`YYYY-MM-DD`). */
function dayRange(start: string, end: string, locale: string): string {
  const to = fmtDayMonth(end, locale, [start, end]);
  return start.slice(0, 7) === end.slice(0, 7)
    ? `${String(Number(start.slice(8, 10)))} – ${to}`
    : `${fmtDayMonth(start, locale, [])} – ${to}`;
}

/** "18 set" / "24 – 26 set": a janela do pedido, para o título do cartão. */
export function conciergeRequestDates(request: ConciergeRequestSource, locale: string): string {
  if (request.productType === 'bus') {
    const { outwardDate, returnDate } = request;
    if (!returnDate || returnDate === outwardDate) return fmtDayMonth(outwardDate, locale);
    return `${fmtDayMonth(outwardDate, locale, [])} – ${fmtDayMonth(returnDate, locale, [outwardDate, returnDate])}`;
  }
  if (request.productType === 'hotel') return dayRange(request.checkIn, request.checkOut, locale);
  if (request.productType === 'rental') {
    return dayRange(request.pickupAt.slice(0, 10), request.dropoffAt.slice(0, 10), locale);
  }
  const first = request.segments[0];
  const last = request.segments.at(-1);
  if (!first) return '';
  if (!last || last.date === first.date) return fmtDayMonth(first.date, locale);
  return `${fmtDayMonth(first.date, locale, [])} – ${fmtDayMonth(last.date, locale, [first.date, last.date])}`;
}

/** A cidade do título: o destino do primeiro trecho (voo), a cidade/lugar da
 *  busca (hotel), o destino da busca (ônibus) ou a cidade do aeroporto de
 *  retirada (carro; outro lugar fica com o rótulo). Degrada para o IATA quando
 *  o catálogo não conhece o aeroporto. */
export function conciergeRequestCity(
  request: ConciergeRequestSource,
  cityOf: (iata: string) => string | undefined,
): string {
  if (request.productType === 'hotel') return request.city ?? request.place;
  if (request.productType === 'bus') return request.to.name;
  if (request.productType === 'rental') {
    const iata = /^airport:([A-Za-z]{3})$/.exec(request.pickup.ref)?.[1];
    return (iata && cityOf(iata.toUpperCase())) ?? request.pickup.label;
  }
  const destination = request.segments[0]?.destination;
  if (!destination) return '';
  return destination.city ?? cityOf(destination.iata) ?? destination.iata;
}

/** O total do painel: só a ida guardada tem valor; o resto é "a cotar". */
export function conciergeRequestTotal(
  request: ConciergeRequestSource,
  copy: ConciergeCopy,
): string {
  const held = request.productType === 'flight' ? request.heldOutbound : undefined;
  return held?.price
    ? copy.panel.totalWithHeld(fmtMoneyWhole(held.price))
    : copy.panel.totalToQuote;
}

/** As linhas que o pedido põe na viagem: a ida já escolhida online (referência
 *  guardada, não emitida), quando há, e o item pedido à equipe (tracejado, "a
 *  cotar" no lugar do valor, selo "Solicitado"). */
export function conciergeRequestRowItems(
  request: ConciergeRequestSource,
  copy: ConciergeCopy,
  locale: string,
): RequestServiceRowItem[] {
  const held = request.productType === 'flight' ? request.heldOutbound : undefined;
  const requested: RequestServiceRowItem = {
    key: 'requested',
    ...requestedRow(request, copy, locale),
    trailing: copy.panel.toQuote,
    badge: { label: copy.panel.requested, tone: 'warning' },
    dashed: true,
  };
  if (!held) return [requested];
  return [
    {
      key: 'held-outbound',
      icon: 'flight',
      title: `${held.segment.from} → ${held.segment.to}`,
      subtitle: [
        fmtTime(held.segment.departAt),
        `${carrierNameOf(held.segment)} ${held.segment.flightNumber}`.trim(),
        held.fareClass,
      ]
        .filter(Boolean)
        .join(' · '),
      ...(held.price ? { trailing: fmtMoneyWhole(held.price) } : {}),
      badge: { label: copy.panel.heldOutbound, tone: 'neutral' },
    },
    requested,
  ];
}

/** O ícone, o título e o subtítulo do item pedido, por produto. */
function requestedRow(
  request: ConciergeRequestSource,
  copy: ConciergeCopy,
  locale: string,
): Pick<RequestServiceRowItem, 'icon' | 'title' | 'subtitle'> {
  switch (request.productType) {
    case 'flight':
      return {
        icon: 'flight',
        title: copy.panel.flightItem(
          request.segments
            .map((segment) => `${segment.origin.iata} → ${segment.destination.iata}`)
            .join(' · '),
        ),
        subtitle: request.segments.map((segment) => fmtDayMonth(segment.date, locale)).join(' · '),
      };
    case 'hotel':
      return {
        icon: 'hotel',
        title: copy.panel.hotelItem,
        subtitle: [conciergeRequestDates(request, locale), request.place].join(' · '),
      };
    case 'bus':
      return {
        icon: 'bus',
        title: copy.panel.busItem(`${request.from.name} → ${request.to.name}`),
        subtitle: conciergeRequestDates(request, locale),
      };
    case 'rental':
      return {
        icon: 'rental',
        title: copy.panel.rentalItem,
        subtitle: [conciergeRequestDates(request, locale), request.pickup.label].join(' · '),
      };
    default: {
      const exhaustive: never = request;
      return exhaustive;
    }
  }
}

/**
 * O que o pedido põe na viagem, como lista pronta, para a tela "Solicitação
 * enviada" do host — dentro de um escopo `genui2`, como todo o kit. O checkout
 * compõe as mesmas linhas junto do que a viagem JÁ tem.
 */
export function ConciergeRequestRows({
  request,
  copy,
  locale,
}: {
  readonly request: ConciergeRequestSource;
  readonly copy: ConciergeCopy;
  readonly locale: string;
}): ReactNode {
  return <RequestServiceRows rows={conciergeRequestRowItems(request, copy, locale)} />;
}
